import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.1. Geometry comes from the §1.4 control-height table, so
// sizes here must stay in lockstep with Input/Select triggers.
//
// Variant naming note: `destructive` maps to the *danger* ramp, not Suzuki Red.
// §1.1 reserves accent-500 (#DE0039) for the portal CTA and sparing emphasis —
// using it for every delete button burns the brand accent and fails the
// "one accent, used sparingly" CI rule. `accent` is the opt-in portal variant.
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 aria-busy:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-brand-600 text-white shadow-card hover:bg-brand-700 hover:shadow-raised active:bg-brand-800 active:scale-[.98] active:shadow-none focus-visible:ring-brand-500',
        outline:
          'border border-line-strong bg-white text-ink shadow-card hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 active:bg-brand-100 focus-visible:ring-brand-500',
        ghost:
          'text-ink-muted hover:bg-surface-subtle hover:text-ink active:bg-line focus-visible:ring-brand-500',
        destructive:
          'bg-danger-500 text-white shadow-card hover:bg-danger-600 hover:shadow-raised active:bg-danger-600 active:scale-[.98] focus-visible:ring-danger-500',
        accent:
          'bg-accent-500 text-white shadow-card hover:bg-accent-600 hover:shadow-raised active:bg-accent-600 active:scale-[.98] focus-visible:ring-accent-500',
        link:
          'text-brand-700 underline-offset-4 hover:text-brand-800 hover:underline focus-visible:ring-brand-500 focus-visible:ring-offset-0',
      },
      size: {
        xs: 'h-6 gap-1.5 rounded-sm px-2 text-xs [&_svg]:size-3',
        sm: 'h-8 px-3 text-sm [&_svg]:size-3.5',
        default: 'h-10 px-4 text-base [&_svg]:size-4',
        lg: 'h-12 rounded-lg px-6 text-lg [&_svg]:size-5',
        'icon-sm': 'h-8 w-8 [&_svg]:size-3.5',
        icon: 'h-10 w-10 [&_svg]:size-4',
        'icon-lg': 'h-12 w-12 rounded-lg [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Swaps the leading icon for a spinner and blocks interaction. The label is
   * deliberately kept so the button never changes width mid-flight (§2.1).
   */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading = false, disabled, children, ...props }, ref) => (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

export { buttonVariants };
